import { useCallback, useEffect, useRef, useState } from "react";
import { sync as defaultSyncApi } from "../services/sync";
import type { SyncStatus } from "../services/syncManager";
import type { SyncQueueEntry } from "../services/db";

export interface SyncStatusApi {
  status: SyncStatus;
  failedEntries(): Promise<SyncQueueEntry[]>;
  retry(id: string): Promise<void>;
  discard(id: string): Promise<void>;
}

const realApi: SyncStatusApi = {
  get status() {
    return defaultSyncApi.status;
  },
  failedEntries: () => defaultSyncApi.failedEntries(),
  retry: (id: string) => defaultSyncApi.retry(id),
  discard: (id: string) => defaultSyncApi.discard(id),
};

export function useSyncStatus(
  api: SyncStatusApi = realApi,
  pollMs: number = 2000,
): {
  status: SyncStatus;
  failed: SyncQueueEntry[];
  refresh(): void;
  retryAll(): Promise<void>;
  discardAll(): Promise<void>;
} {
  const [status, setStatus] = useState<SyncStatus>(() => api.status);
  const [failed, setFailed] = useState<SyncQueueEntry[]>([]);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      const [s, entries] = await Promise.all([
        Promise.resolve(api.status),
        api.failedEntries(),
      ]);
      if (!mountedRef.current) return;
      setStatus(s);
      setFailed(entries);
    } catch {
      // The sync engine may not be initialised in some test paths.
    }
  }, [api]);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    const id = setInterval(() => void refresh(), pollMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh, pollMs]);

  const retryAll = useCallback(async () => {
    await Promise.all(failed.map((e) => api.retry(e.id)));
    await refresh();
  }, [failed, api, refresh]);

  const discardAll = useCallback(async () => {
    await Promise.all(failed.map((e) => api.discard(e.id)));
    await refresh();
  }, [failed, api, refresh]);

  return { status, failed, refresh, retryAll, discardAll };
}
